import { Descriptions } from 'antd';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CgCloseR } from 'react-icons/cg';
import { useSelector } from 'react-redux';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  createInquiryAnswer,
  getBusinessInquiry,
  getInquiryAnswer,
  updateInquiryAnswer,
} from '../../../api/franchise.api';
import Button from '../../../components/Button';
import ErrorResponse from '../../../components/ErrorResponse';
import FileUpload from '../../../components/FileUpload';
import SimpleHeader from '../../../components/layout/SimpleHeader';
import Loader from '../../../components/Loader';
import useAuth from '../../../hooks/useAuth';
import useAxios from '../../../hooks/useAxios';
import useNotification from '../../../hooks/useNotification';
import { getCommonCodeLabel } from '../../../lib/store';
import {
  IGetBusinessInquiry,
  IGetInquiryAnswer,
} from '../../../models/franchise.model';
import { RootState } from '../../../store/rootReducer';
import TableEmpty from '../../../components/common/TableEmpty';

interface IMatch {
  inquiryCode: string;
}

const BusinessInqueryRead = () => {
  const { t } = useTranslation();
  const { writeAuthYn } = useAuth();
  const match = useRouteMatch<IMatch>();
  const inquiryCode = Number(match.params.inquiryCode);
  const history = useHistory();
  const location = useLocation();
  const { infoNotification, successNotification } = useNotification();
  const { auth } = useSelector((state: RootState) => state.auth);
  const { serverDate } = useSelector((state: RootState) => state.serverDate);  
  const {
    data: res,
    isLoading,
    error,
    execute,
  } = useAxios<IGetBusinessInquiry>();
  const {
    data: res2,
    isLoading: isLoading2,
    error: error2,
    execute: execute2,
  } = useAxios<IGetInquiryAnswer>();
  const {
    data: submitRes,
    isLoading: submitIsLoading,
    error: submitError,
    execute: submitExecute,
  } = useAxios();

  const [isAnswerMode, setIsAnswerMode] = useState(false);
  const [answerString, setAnswerString] = useState('');
  const [file, setFile] = useState<File>();
  const [answerFile, setAnswerFile] = useState<any>();

  useEffect(() => {
    if (inquiryCode) {
      execute(getBusinessInquiry(inquiryCode));
      execute2(getInquiryAnswer(inquiryCode));
    }
  }, [inquiryCode]);

  useEffect(() => {
    if (submitRes && !submitIsLoading) {
      execute2(getInquiryAnswer(inquiryCode));
      successNotification(t('common.message.save1'));
      setIsAnswerMode(false);
    }
  }, [submitRes, submitIsLoading]);

  const onEditAnswer = () => {
    if (res2?.data?.regMemCode) {
      setAnswerString(res2.data.answerString);
      setAnswerFile(res2.data.file);
    }
    setIsAnswerMode(true);
  };

  const onSubmitAnswer = () => {
    if (!answerString) {
      return infoNotification(t('board.inquiry.message1'));
    }

    const form = new FormData();
    form.append('answerString', answerString);

    if (res2?.data?.regMemCode) {
      // 수정 시 파일 처리
      if (res2?.data?.answerFileCode) {
        form.append('answerFileCode', String(res2.data.answerFileCode));
      }

      if (answerFile) {
        // 기존 첨부파일 있음
        if (file) form.append('fileStatus', 'update');
        else if (!answerFile.originalName) form.append('fileStatus', 'delete');
        else form.append('fileStatus', 'keep');
      } else {
        // 기존 첨부파일 없음
        if (file) form.append('fileStatus', 'create');
        else form.append('fileStatus', 'keep');
      }
    }

    if (file) form.append('file', file);

    res2?.data?.regMemCode
      ? submitExecute(updateInquiryAnswer(inquiryCode, form))
      : submitExecute(createInquiryAnswer(inquiryCode, form));
  };

  return (
    <div className="content scrollbar">
      {isLoading || isLoading2 || submitIsLoading ? <Loader /> : null}
      <ErrorResponse error={error} />
      <ErrorResponse error={error2} />
      <ErrorResponse error={submitError} />
      <SimpleHeader
        iconFile="icon-business-big.svg"
        parentMenu={t('common.menu.business')}
        currentMenu={t('business.inquiry.title2')}
      />

      <div>
        <div className='comm-table__area rowType mt-0'>
          <Descriptions
            bordered
            column={4}
            size="small"
            labelStyle={{ width: '12.5%' }}
            contentStyle={{ width: '12.5%' }}
          >
            <Descriptions.Item label={t('business.inquiry.column2')}>
              {res?.data?.representName}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column7')}>
              {moment(res?.data?.regDate).format('YYYY.MM.DD')}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column3')}>
              {res?.data?.mobilePhone}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column4')}>
              {res?.data?.email}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column8')}>
              {getCommonCodeLabel('SY0004', res?.data?.sidoCode || '', 'ko')}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column9')}>
              {`${res?.data?.wishYear}${t('business.inquiry.1')} ${res?.data?.wishMonth}${t('business.inquiry.2')}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column10')}>
              {getCommonCodeLabel('BR0012', res?.data?.adChannelCode || '', 'ko')}
              {res?.data?.etcDescription ? ` (${res?.data?.etcDescription})` : ''}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column11')}>
              {getCommonCodeLabel('BR0013', res?.data?.answerDiv || '', 'ko')}
            </Descriptions.Item>
            <Descriptions.Item label={t('business.inquiry.column12')}>
              <div style={{ whiteSpace: 'pre-wrap' }}>{res?.data?.content}</div>
            </Descriptions.Item>
          </Descriptions>
        </div>

        <div className='comm-table__area rowType'>
        {
          isAnswerMode
            ? (
              // 답변 등록/수정
              <Descriptions
                bordered
                column={2}
                size="small"
                labelStyle={{ width: '15%' }}
                contentStyle={{ width: '35%' }}
              >
                <Descriptions.Item label={t('business.inquiry.3')}>
                  {res2?.data?.regMemCode
                    ? res2?.data?.regMemKorName
                    : auth?.korName}
                </Descriptions.Item>
                <Descriptions.Item label={t('business.inquiry.4')}>
                  {res2?.data?.answerRegDate
                    ? moment(res2?.data?.answerRegDate).format(
                        'YYYY.MM.DD HH:MM:SS',
                      )
                    : moment(serverDate).format('YYYY.MM.DD')}
                </Descriptions.Item>
                <Descriptions.Item label={t('business.inquiry.5')} span={2}>
                  <textarea
                    className="w-100"
                    rows={6}
                    value={answerString}
                    onChange={(e) => setAnswerString(e.target.value)}
                  />
                </Descriptions.Item>
                <Descriptions.Item label={t('archive.exam.column7')} span={2}>
                  {answerFile?.originalName ? (
                    <div>
                      <a
                        className="download-link"
                        href={`${answerFile.fileLocation}`}
                        download={answerFile.originalName}
                      >
                        {answerFile.originalName}
                      </a>
                      <span
                        className="ml-1 pointer"
                        onClick={() =>
                          setAnswerFile({ ...answerFile, originalName: null })
                        }
                      >
                        <CgCloseR />
                      </span>
                    </div>
                  ) : null}
                  <FileUpload file={file} setFile={(file: File) => setFile(file)} />
                </Descriptions.Item>
              </Descriptions>
              )
            : res2?.data?.regMemCode
              ? (
                // 등록된 답변 보기
                <Descriptions bordered column={2} size="small" labelStyle={{ width: '15%' }} contentStyle={{ width: '35%' }}>
                  <Descriptions.Item label={t('business.inquiry.6')}>
                    {res2?.data?.regMemKorName}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('business.inquiry.4')}>
                    {moment(res2?.data?.answerRegDate).format('YYYY.MM.DD HH:MM:SS')}
                  </Descriptions.Item>
                  <Descriptions.Item label={t('business.inquiry.5')} span={2}>
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {res2?.data?.answerString}
                    </div>
                  </Descriptions.Item>
                  {res2?.data?.file ? (
                    <Descriptions.Item label={t('archive.exam.column7')} span={2}>
                      <a
                        className="download-link mr-1"
                        href={res2.data.file.fileLocation}
                        download={res2.data.file.originalName}
                      >
                        {res2.data.file.originalName}
                      </a>
                    </Descriptions.Item>
                  ) : null}
                </Descriptions>
                )
              : (
                <TableEmpty text={t('business.inquiry.7')} />
                )
        }
        </div>

        <div className="comm-bx__between">
          <div className="comm-btn__area">
            <Button
              className="btn-basic gray-line"
              onClick={() =>
                history.push(`/business/inquiry${location.search}`)
              }
              text={t('common.button.list')}
            />
          </div>
          <div className="comm-btn__area right">
            {isAnswerMode ? (
              <Button
                className="btn-basic pink"
                onClick={onSubmitAnswer}
                text={t('common.button.save')}
                writeAuthYn={writeAuthYn}
              />
            ) : (
              <Button
                className="btn-basic pink-line"
                onClick={onEditAnswer}
                text={res2?.data?.regMemCode ? t('business.inquiry.8') : t('business.inquiry.9')}
                writeAuthYn={writeAuthYn}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BusinessInqueryRead;
